/**
 * WDFetchUpload - Event-driven file upload class
 * 
 * Uses PUT requests to upload single files with comprehensive event tracking.
 * Extends EventTarget to provide clean event-driven progress monitoring.
 * 
 * Usage:
 *   const uploader = new WDFetchUpload('/api/upload');
 *   uploader.file = fileInput.files[0];
 *   
 *   uploader.addEventListener('upload:start', (e) => {
 *     console.log('Upload started');
 *   });
 *   
 *   uploader.addEventListener('upload:progress', (e) => {
 *     const { loaded, total, percent } = e.detail;
 *     console.log(`Progress: ${percent.toFixed(1)}%`);
 *   });
 *   
 *   uploader.addEventListener('upload:success', (e) => {
 *     console.log('Upload completed:', e.detail.response);
 *   });
 *   
 *   uploader.addEventListener('upload:error', (e) => {
 *     console.error('Upload failed:', e.detail.error);
 *   });
 *   
 *   uploader.put().then(result => {
 *     // Also available as Promise
 *   }).catch(error => {
 *     // Handle errors
 *   });
 * 
 * Events fired during upload process:
 * - 'upload:start': Upload begins (detail: { file, event })
 * - 'upload:progress': Progress updates (detail: { file, event, loaded, total, percent })
 * - 'upload:success': Upload completed successfully (detail: { file, response, status, statusText })
 * - 'upload:error': Upload failed (detail: { file, response, error, status, statusText })
 * - 'upload:abort': Upload was cancelled (detail: { file, event, error })
 */

import { WDModel } from '../wd-model'
import WDUtil from '../wd-util';

export default class WDFetchUpload extends EventTarget
{
	#config = {
		contentType: 'application/json',
		auth: {},
		reqURL: null,
		reqURI: null,
		allowMultipleRequests: false,
		headers: {},
	};

	#data = {};

	constructor(config, values)
	{
		super();

		if (typeof (config) == 'string') 
		{
			this.#config.reqURI = config;
		}
		else if (typeof (config) == 'object')
		{
			WDUtil.merge(this.#config, config);
		}

		if (values) {
			this.assign(values);
		}
	}

	// Data management methods (similar to WDModel)
	assign(obj) {
		if (obj && typeof obj === 'object') {
			Object.assign(this.#data, obj);
		}
		return this;
	}

	toObject() {
		return { ...this.#data };
	}

	// File property accessor
	get file() {
		return this.#data.file;
	}

	set file(value) {
		this.#data.file = value;
	}

	// Current request reference for abortion
	#currentRequest = null;

	// Backwards compatibility
	add(obj) {
		return this.assign(obj);
	}

	// Check if upload is in progress
	get isUploading() {
		return this.#currentRequest !== null;
	}

	// Abort current upload
	abort() {
		if (this.#currentRequest) {
			this.#currentRequest.abort();
			this.#currentRequest = null;
		}
		return this;
	}

	buildResponseContentType(response)
	{
		const ct = response.headers?.get("content-type");
		const pos = ct?.indexOf(';');
		return (pos !== -1) ? ct.substr(0, pos) : ct;
	}

	/**
	 * since we inherit WDProxy, we can't use normal inheritance
	 * @returns 
	 */
	getConfig() 
	{
		return this.#config;
	};

	getReqURI() 
	{
		return this.getConfig().reqURI || '';
	};

	setReqURI(uri)
	{
		this.getConfig().reqURI = uri;
		return this;
	}

	getReqURL() { return this.getConfig().reqURL || ''; };
	setReqURL(url)
	{
		this.getConfig().reqURL = url;
		return this;
	};



	buildURI(addURI)
	{
		let uri = '';

		//given a base uri to work with
		if (this.#config?.reqURI) uri += this.#config.reqURI;

		//given a supplemental uri to the base url and base uri
		if (addURI) uri += addURI;

		return uri;
	};

	buildURL(uri)
	{
		let url = '';
		if (this.#config.reqURL) url += this.#config.reqURL;

		const addURI = this.buildURI(uri);
		if (addURI) url += addURI;

		return url;
	};

	buildAuth(request)
	{
		if (this.#config.auth)
		{
			const auth = this.#config.auth;
			request.withCredentials = true;

			if (auth.mode === 'basic')
			{
				const authStr = 'Basic ' + btoa(auth.login + ':' + auth.password);
				request.setRequestHeader('Authorization', authStr);
			}
			else if (auth.mode === 'digest')
			{
				// Future implementation
			}
		}
	}

	getFormData()
	{	
		const formData = new FormData();
		formData.append('file', this.file);

		const formObj = this.toObject();

		for (let key in formObj)
		{
			if (key != 'file' && typeof (formObj[key]) != 'object') 
			{
				formData.append(key, formObj[key]);
			}
		}

		return formData;
	};

	/**
	 * Build XMLHttpRequest with event listeners for upload progress
	 */
	buildRequest(method, uri)
	{
		const url = this.buildURL(uri);

		if (WDConfig?.debug?.fetch) {
			console.log('Upload Request', method, url);
		}

		const request = new XMLHttpRequest();
		request.open(method, url);
		request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

		for (let key in this.#config.headers)
		{
			request.setRequestHeader(key, this.#config.headers[key]);
		}

		//add authorization		
		this.buildAuth(request);

		// Add upload event listeners for progress tracking
		if (method === 'PUT' && request.upload)
		{
			request.upload.addEventListener('loadstart', (evt) => {
				this.dispatchEvent(new CustomEvent('upload:start', {
					detail: { file: this.file, event: evt }
				}));
			});

			request.upload.addEventListener('progress', (evt) => {
				if (evt.lengthComputable) {
					const percentComplete = (evt.loaded / evt.total) * 100;
					
					// Update file progress properties
					if (this.file) {
						this.file.loaded = evt.loaded;
						this.file.total = evt.total;
						this.file.percent_loaded = percentComplete;
					}

					this.dispatchEvent(new CustomEvent('upload:progress', {
						detail: { 
							file: this.file, 
							event: evt,
							loaded: evt.loaded,
							total: evt.total,
							percent: percentComplete
						}
					}));
				}
			});

			request.upload.addEventListener('abort', (evt) => {
				this.dispatchEvent(new CustomEvent('upload:abort', {
					detail: { file: this.file, event: evt }
				}));
			});
		}

		return request;
	}

	put(uri)
	{
		return new Promise((resolve, reject) =>
		{
			// Abort any existing request
			this.abort();

			//build the request
			const request = this.buildRequest('PUT', uri);
			this.#currentRequest = request;
			
			// upload completed successfully
			request.addEventListener('load', (evt) =>
			{
				const respData = this.parseResponse(request);
				
				// Update file progress to complete
				if (this.file) {
					this.file.loaded = this.file.size || this.file.total;
					this.file.percent_loaded = 100;
				}

				// Check if response indicates an error
				if (request.status >= 400 || respData?.error || respData?.message) {
					const errorResult = {
						response: respData,
						file: this.file,
						event: evt,
						status: request.status,
						statusText: request.statusText
					};

					// Fire error event
					this.dispatchEvent(new CustomEvent('upload:error', {
						detail: errorResult
					}));

					reject(errorResult);
				}
				else
				{
					const successResult = {
						response: respData,
						file: this.file,
						event: evt,
						status: request.status,
						statusText: request.statusText
					};

					// Fire success event
					this.dispatchEvent(new CustomEvent('upload:success', {
						detail: successResult
					}));

					resolve(successResult);
				}
			});

			// Network or other errors
			request.addEventListener('error', (evt) =>
			{
				const errorResult = {
					response: this.parseResponse(request),
					file: this.file,
					event: evt,
					status: request.status,
					statusText: request.statusText,
					error: 'Network error or request failed'
				};

				// Fire error event
				this.dispatchEvent(new CustomEvent('upload:error', {
					detail: errorResult
				}));

				reject(errorResult);
			});

			// Handle request abortion
			request.addEventListener('abort', (evt) =>
			{
				const abortResult = {
					response: null,
					file: this.file,
					event: evt,
					error: 'Upload was cancelled'
				};

				// Fire abort event
				this.dispatchEvent(new CustomEvent('upload:abort', {
					detail: abortResult
				}));

				reject(abortResult);
			});
	
			// Clean up request reference when complete
			const cleanup = () => {
				if (this.#currentRequest === request) {
					this.#currentRequest = null;
				}
			};

			// Add cleanup to all completion paths
			request.addEventListener('load', cleanup);
			request.addEventListener('error', cleanup);
			request.addEventListener('abort', cleanup);

			// Send the request with form data
			request.send(this.getFormData());
		});
	}

	parseResponse(request)
	{
		let resp = null;

		try
		{
			// Try to parse as JSON first
			if (request.response && typeof request.response === 'string') {
				resp = JSON.parse(request.response);
			} else {
				resp = request.response;
			}
		}
		catch (err)
		{
			// If JSON parsing fails, return raw response
			resp = request.response || request.responseText || null;
		}

		return resp;
	}

	/**
	 * Cleanup method to abort pending uploads and clear resources
	 */
	destroy() {
		// Abort any pending upload
		this.abort();
		
		// Clear current request reference
		this.#currentRequest = null;
		
		// Clear configuration
		this.#config = null;
		
		// Clear data
		this.#data = null;
		
		// Note: EventTarget listeners are automatically cleaned up
		// when the object is garbage collected, but callers should
		// remove their event listeners before calling destroy()
		
		return this;
	}

}

